import React from 'react';
import { cn } from '../../utils/cn';

interface PageLayoutProps {
  children: React.ReactNode;
  className?: string;
  fullBleed?: boolean;
  noMaxWidth?: boolean;
  noBottomPadding?: boolean;
}

/**
 * Standard page layout wrapper.
 *
 * - Provides consistent horizontal padding (`px-4 sm:px-6 lg:px-8`)
 * - Centers content with `max-w-[1440px] mx-auto` (unless `noMaxWidth`)
 * - Adds bottom padding for the fixed bottom nav (`pb-32` unless `noBottomPadding`)
 * - Adds top padding equal to the fixed header height (`pt-[--header-height]`)
 *
 * Use `fullBleed` for pages where the hero/cover should span edge-to-edge
 * while the rest of the content stays constrained (e.g. Profile).
 */
export const PageLayout: React.FC<PageLayoutProps> = ({
  children,
  className,
  fullBleed = false,
  noMaxWidth = false,
  noBottomPadding = false,
}) => {
  return (
    <div
      className={cn(
        'min-h-screen bg-black text-white',
        !noBottomPadding && 'pb-32',
        'pt-[var(--header-height,5.5rem)]',
        className,
      )}
    >
      {fullBleed ? (
        <div className="relative">
          {/* Full-bleed area (hero, cover images, etc.) */}
          <div className="w-full">{children}</div>
        </div>
      ) : (
        <div className={cn('px-4 sm:px-6 lg:px-8', !noMaxWidth && 'max-w-[1440px] mx-auto')}>
          {children}
        </div>
      )}
    </div>
  );
};
